import { useMemo } from "react";
import { Link } from "react-router-dom";
import { IndianRupee, ShieldCheck, MessageCircle, Phone, CheckCircle2, AlertCircle } from "lucide-react";
import { CLINIC, waLink, telLink } from "../lib/clinic";
import { usePageMeta, usePageJsonLd } from "../hooks/usePageMeta";
import { SITE, breadcrumb, graph, CLINIC_NODE } from "../lib/schema";
import SectionHeader from "../components/site/SectionHeader";

// Money-intent landing page — targets "cataract surgery cost in varanasi",
// "cataract operation price in varanasi", "मोतियाबिंद ऑपरेशन खर्च".
// Uses honest range-based pricing (transparent + protects against
// undercutting by chains promising unrealistic quotes).

const IOL_TIERS = [
  {
    tier: "Standard monofocal IOL",
    range: "₹18,000 – ₹25,000",
    ayushman: false,
    goodFor: "Patients happy with reading glasses; single-focus vision (distance OR near).",
    covered: "Cashless with most private TPAs — policies typically cover a package of approx ₹25,000–30,000.",
  },
  {
    tier: "Aspheric monofocal (premium optics)",
    range: "₹25,000 – ₹35,000",
    ayushman: false,
    goodFor: "Patients wanting sharper night driving vision; single-focus.",
    covered: "Cashless with most private insurance policies — your policy covers approx ₹25,000–30,000 and the difference is borne by you, depending on final authorisation by the insurer.",
  },
  {
    tier: "Extended monofocal (functional near-intermediate)",
    range: "₹40,000 – ₹45,000",
    ayushman: false,
    goodFor: "Patients wanting slightly extended range beyond a standard monofocal, without upgrading to EDOF.",
    covered: "Cashless with private policies — the difference above your policy's package (approx ₹25,000–30,000) is borne by you, depending on final authorisation by the insurer.",
  },
  {
    tier: "Toric monofocal (astigmatism correction)",
    range: "₹40,000 – ₹60,000",
    ayushman: false,
    goodFor: "Patients with >1.5 D corneal astigmatism who want reduced spectacle dependence for distance vision.",
    covered: "Cashless with most private policies — the difference above your policy's package (approx ₹25,000–30,000) is borne by you, depending on final authorisation by the insurer.",
  },
  {
    tier: "EDOF (Extended Depth-of-Focus)",
    range: "₹50,000 – ₹1,00,000",
    ayushman: false,
    goodFor: "Patients wanting distance + intermediate (dashboard, computer) with reduced dependence on glasses.",
    covered: "Cashless approvals available — the difference above your policy's package (approx ₹25,000–30,000) is borne by you, depending on final authorisation by the insurer.",
  },
  {
    tier: "Trifocal / Panoptix (multifocal)",
    range: "₹70,000 – ₹1,10,000 per eye",
    ayushman: false,
    goodFor: "Patients wanting reduced spectacle dependence at distance, intermediate and near.",
    covered: "Cashless on most policies — the difference above your policy's package (approx ₹25,000–30,000) is borne by you, depending on final authorisation by the insurer.",
  },
];

const INCLUDED = [
  "The surgical procedure — Micro-Incision Cataract Surgery (MICS) via 2.2 mm phacoemulsification",
  "The IOL (intraocular lens) at the tier chosen",
  "Topical anaesthesia (drops only, no injection); same-day discharge for uncomplicated cases",
  "Operating theatre + consumables charges",
  "Surgeon and support-staff fees",
];

const NOT_INCLUDED = [
  "Pre-operative biometry (IOL power calculation) — billed separately as an OPD investigation",
  "OPD consultation visits (before and after surgery)",
  "Post-operative eye drops and other medicines",
  "YAG capsulotomy laser (if needed later, usually 6-24 months after surgery for capsular opacification)",
  "Systemic pre-anaesthesia workup (usually done by patient's physician)",
  "Complications from uncontrolled diabetes or hypertension not disclosed pre-op",
  "Bilateral surgery on the same day (billed separately per eye)",
];

const CataractCost = () => {
  usePageMeta({
    title: "Cataract Surgery Cost in Varanasi | Prices, IOL Options & Insurance",
    path: "/cataract-surgery-cost-in-varanasi",
    description:
      "Transparent cataract surgery pricing in Varanasi at Shantanu Netralaya — from ₹18,000 (standard monofocal) to ₹1.1 lakh (trifocal IOL). MICS phaco, same-day discharge, cashless insurance accepted. Biometry, medicines, OPD visits and YAG capsulotomy billed separately.",
  });

  const jsonLd = useMemo(() => graph([
    breadcrumb([
      { name: "Home", path: "/" },
      { name: "Cataract Surgery Cost in Varanasi", path: "/cataract-surgery-cost-in-varanasi" },
    ]),
    CLINIC_NODE,
    {
      "@type": "FAQPage",
      "@id": `${SITE}/cataract-surgery-cost-in-varanasi#faq`,
      mainEntity: [
        { "@type": "Question", name: "How much does cataract surgery cost in Varanasi?", acceptedAnswer: { "@type": "Answer", text: "Cataract surgery cost in Varanasi ranges from ₹18,000-25,000 (standard monofocal IOL) to ₹70,000-1,10,000 per eye (premium trifocal IOL). Aspheric monofocal ₹25,000-35,000, extended monofocal ₹40,000-45,000, toric monofocal ₹40,000-60,000, EDOF ₹50,000-1,00,000. Prices are for the surgery + IOL only; biometry, OPD visits, medicines and YAG capsulotomy are billed separately." } },
        { "@type": "Question", name: "Is cataract surgery covered under Ayushman Bharat in Varanasi?", acceptedAnswer: { "@type": "Answer", text: "Yes — Shantanu Netralaya is empanelled under Ayushman Bharat (PM-JAY). The scheme covers only an IOL implant worth approximately ₹3,000 for eligible beneficiaries. Which option applies to your case and any out-of-pocket amount is clarified at the hospital — message us on WhatsApp before you visit." } },
        { "@type": "Question", name: "Which IOL is best for cataract surgery?", acceptedAnswer: { "@type": "Answer", text: "There's no universal 'best' IOL — the right choice depends on your lifestyle. If you're happy with reading glasses, a standard monofocal is fine. If you want to eliminate glasses entirely, a trifocal or EDOF is better. If you have significant astigmatism, a toric IOL is essential." } },
        { "@type": "Question", name: "Is same-day discharge safe after cataract surgery?", acceptedAnswer: { "@type": "Answer", text: "Same-day discharge is the current international standard for uncomplicated cataract surgery under topical anaesthesia. For most patients, this involves arriving in the morning, surgery, a couple of hours of observation and discharge by early afternoon. Cases with additional risk factors may be advised a longer observation or overnight stay." } },
      ],
    },
  ]), []);
  usePageJsonLd(jsonLd);

  return (
    <div data-testid="cataract-cost-page">
      <section className="pt-20 pb-8 container-page">
        <div className="overline mb-4 inline-flex items-center gap-2">
          <IndianRupee size={14} strokeWidth={1.5} /> Transparent Pricing
        </div>
        <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl leading-[1.05] text-balance max-w-3xl">
          Cataract Surgery Cost in Varanasi — Every IOL Option, Every Price Tier
        </h1>
        <p className="mt-6 text-base sm:text-lg text-muted-foreground max-w-2xl">
          Honest, itemised cataract surgery pricing at Shantanu Netralaya. Ranges reflect the IOL brand tier + insurance category — not hidden fees. Ayushman Bharat and all major cashless TPAs accepted.
        </p>
        <div className="mt-6 flex flex-wrap gap-3">
          <a href={waLink("Hi, I'd like a cataract surgery cost estimate for my case.")} target="_blank" rel="noopener noreferrer" className="btn-primary" data-testid="cost-cta-whatsapp">
            <MessageCircle size={16} /> Get a personalised quote
          </a>
          <a href={telLink()} className="btn-ghost" data-testid="cost-cta-call"><Phone size={14} /> {CLINIC.phone}</a>
        </div>
      </section>

      <section className="py-10 border-t border-border container-page" data-testid="cost-tiers">
        <SectionHeader overline="IOL tier pricing" title="Cost by IOL choice, not by upselling" />
        <div className="mt-8 space-y-4">
          {IOL_TIERS.map((t) => (
            <div key={t.tier} className="rounded-2xl border border-border p-5 bg-background grid md:grid-cols-4 gap-4">
              <div className="md:col-span-2">
                <div className="font-serif text-xl">{t.tier}</div>
                <p className="text-sm text-muted-foreground mt-1">{t.goodFor}</p>
              </div>
              <div className="text-2xl font-serif text-[hsl(var(--primary))]">{t.range}</div>
              <div className="text-xs text-muted-foreground">
                {t.ayushman && (<div className="inline-flex items-center gap-1.5 mb-1.5 text-[hsl(var(--accent))]"><CheckCircle2 size={12} /> Ayushman Bharat eligible</div>)}
                <div>{t.covered}</div>
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="py-14 border-t border-border container-page" data-testid="cost-included">
        <div className="grid md:grid-cols-2 gap-10">
          <div>
            <div className="overline mb-3 inline-flex items-center gap-2"><CheckCircle2 size={13} /> What the price INCLUDES</div>
            <ul className="mt-3 space-y-2 text-sm">
              {INCLUDED.map((i) => (<li key={i} className="flex items-start gap-2"><CheckCircle2 size={15} className="text-[hsl(var(--accent))] mt-0.5 shrink-0" /><span>{i}</span></li>))}
            </ul>
          </div>
          <div>
            <div className="overline mb-3 inline-flex items-center gap-2"><AlertCircle size={13} /> What is NOT included</div>
            <ul className="mt-3 space-y-2 text-sm">
              {NOT_INCLUDED.map((i) => (<li key={i} className="flex items-start gap-2"><AlertCircle size={15} className="text-[hsl(var(--muted-foreground))] mt-0.5 shrink-0" /><span>{i}</span></li>))}
            </ul>
          </div>
        </div>
      </section>

      <section className="py-14 border-t border-border container-page" data-testid="cost-calc-cta">
        <div className="rounded-2xl border border-[hsl(var(--accent))]/25 bg-[hsl(var(--accent))]/5 p-6 md:p-8 flex flex-col md:flex-row md:items-center gap-6">
          <div className="flex-1">
            <div className="overline mb-2 inline-flex items-center gap-2">Interactive calculator</div>
            <h3 className="font-serif text-2xl leading-tight">Get an instant estimate — try the calculator</h3>
            <p className="mt-2 text-sm text-muted-foreground max-w-xl">
              Pick your IOL tier, insurance scenario and one/both eyes. The interactive calculator returns a live out-of-pocket range in seconds.
            </p>
          </div>
          <Link to="/tools/cataract-cost-calculator" className="btn-primary text-xs inline-flex items-center gap-2 shrink-0" data-testid="cost-page-calc-cta">
            Open the calculator
          </Link>
        </div>
      </section>

      <section className="py-14 border-t border-border container-page" data-testid="cost-iol-tool-cta">
        <div className="rounded-2xl border border-[hsl(var(--primary))]/25 bg-[hsl(var(--primary))]/5 p-6 md:p-8 flex flex-col md:flex-row md:items-center gap-6">
          <div className="flex-1">
            <div className="overline mb-2 inline-flex items-center gap-2">Interactive tool</div>
            <h3 className="font-serif text-2xl leading-tight">Not sure which IOL fits your lifestyle & budget?</h3>
            <p className="mt-2 text-sm text-muted-foreground max-w-xl">
              Our free 3-5 minute IOL selector suite recommends the right lens category based on your daily habits, driving patterns and reading needs — reviewed by Dr. Shantanu Kumar Gupta.
            </p>
          </div>
          <Link to="/iol-selector" className="btn-primary text-xs inline-flex items-center gap-2 shrink-0" data-testid="cost-page-iol-cta">
            Try the IOL selector
          </Link>
        </div>
      </section>

      <section className="py-14 border-t border-border container-page" data-testid="cost-insurance">
        <SectionHeader overline="Insurance" title="Cashless insurance & Ayushman Bharat" />
        <p className="mt-4 text-sm text-muted-foreground max-w-2xl">
          We handle the paperwork before your surgery date. Bring your policy card and government ID; our front desk arranges pre-authorisation with your TPA typically within 24-48 hours. Most private policies cover a cataract package of approx ₹25,000–30,000. For any package above that, the difference is borne by you — the exact amount depends on your individual policy and the final authorisation given by the insurance company, which we obtain in writing before surgery.
        </p>
        <div className="mt-6">
          <Link to="/insurance" className="text-sm text-[hsl(var(--primary))] underline decoration-dotted underline-offset-4">
            → See the full list of accepted insurers
          </Link>
        </div>
      </section>

      <section className="py-14 border-t border-border bg-[hsl(var(--secondary))]/40" data-testid="cost-cta-bottom">
        <div className="container-page rounded-3xl border border-border bg-background p-10 text-center">
          <div className="overline mb-3 inline-flex items-center gap-2"><ShieldCheck size={13} /> No surprise bills</div>
          <h2 className="font-serif text-3xl">Get a written estimate before you decide</h2>
          <p className="mt-3 max-w-lg mx-auto text-sm text-muted-foreground">
            Share your prescription or biometry (if done) and we'll email you an itemised estimate with your IOL options.
          </p>
          <div className="mt-6 flex flex-wrap justify-center gap-3">
            <a href={waLink("Hi, I'd like a written cataract-surgery estimate.")} target="_blank" rel="noopener noreferrer" className="btn-primary" data-testid="cost-cta-bottom-whatsapp">
              <MessageCircle size={16} /> WhatsApp for estimate
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default CataractCost;
